import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { PRODUCT_CATEGORY_LABELS, type ProductCategory, type ProductSummary } from '@va/shared';
import { useProducts } from '../api/queries';
import { IconCard, IconCheck, IconMonitor, IconSearch, IconShield } from '../components/icons';
import { ProductCard } from '../components/ProductCard';
import { Empty, ErrorBox, Price } from '../components/ui';
import { isDesktop, mediaUrl } from '../platform';

/** Products per page of the catalog: 3 rows of 4 on a wide screen. */
const PAGE_SIZE = 12;

const FILTERS: { key: string; label: string; match: (c: ProductCategory) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'effects', label: 'Effects', match: (c) => c === 'ffgl-effect' },
  { key: 'sources', label: 'Sources', match: (c) => c === 'ffgl-source' },
  { key: 'wire', label: 'Wire', match: (c) => c === 'wire' },
  { key: 'apps', label: 'Apps', match: (c) => c === 'app' },
  { key: 'bundles', label: 'Bundles', match: (c) => c === 'bundle' },
];

function Hero({ product, loading }: { product: ProductSummary | undefined; loading: boolean }) {
  const loc = useLocation();
  return (
    <section className="hero">
      <div>
        <span className="eyebrow">Visual Artform · Plugins for Resolume</span>
        <h1>
          Effects built for the <em>stage</em>, not the timeline.
        </h1>
        <p className="hero-lead">
          Camera effects, live sources, Wire patches and tools for Resolume. Install anything free with a moving watermark, then buy once to unlock it on all your computers.
        </p>
        <div className="row wrap">
          {isDesktop ? (
            <Link to="/library" className="btn btn-primary btn-lg">My Library</Link>
          ) : (
            <Link to="/download" className="btn btn-primary btn-lg">Get the app</Link>
          )}
        </div>
        <div className="trust-row">
          <span><IconCheck size={16} /> Try everything free</span>
          <span><IconMonitor size={16} /> Windows &amp; macOS</span>
          <span><IconShield size={16} /> Licenses on up to 3 computers</span>
          <span><IconCard size={16} /> Credit card or PayPal</span>
        </div>
      </div>
      {/* While the products load, a placeholder of the same size: the list below doesn't move. */}
      {!product && loading && <div className="hero-art skeleton" style={{ borderRadius: 'var(--va-radius-lg)' }} aria-hidden="true" />}
      {product && (
        <Link to={`/products/${product.slug}`} state={{ from: loc.pathname + loc.search }} className="hero-art" aria-label={`${product.name}: ${product.tagline}`}>
          {product.previewVideoUrl ? (
            <video src={mediaUrl(product.previewVideoUrl)} poster={mediaUrl(product.thumbnailUrl)} autoPlay muted loop playsInline />
          ) : (
            product.thumbnailUrl && <img src={mediaUrl(product.thumbnailUrl)} alt="" />
          )}
          <div className="hero-art-caption">
            <div>
              <span className="pill indigo" style={{ marginBottom: 8 }}>Featured</span>
              <div className="title">{product.name}</div>
              <div className="small" style={{ color: 'var(--va-body)' }}>{product.tagline}</div>
            </div>
            <span className="pill solid">
              <Price {...product} />
            </span>
          </div>
        </Link>
      )}
    </section>
  );
}

/** Page numbers under the catalog, shown once there's more than one page. */
function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  return (
    <nav className="pager" aria-label="Pages">
      <button className="btn btn-sm btn-ghost" disabled={page === 1} onClick={() => onPage(page - 1)}>‹ Previous</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
        <button key={n} className={`btn btn-sm ${n === page ? 'btn-primary' : 'btn-outline'}`} aria-current={n === page ? 'page' : undefined} onClick={() => onPage(n)}>
          {n}
        </button>
      ))}
      <button className="btn btn-sm btn-ghost" disabled={page === pages} onClick={() => onPage(page + 1)}>Next ›</button>
    </nav>
  );
}

export function StorePage() {
  const { data, error, isLoading, refetch } = useProducts();
  // Category, search and page are in the address (?cat=effects&q=blur&page=2), so going back
  // from a product returns to the same list.
  const [params, setParams] = useSearchParams();
  const filter = FILTERS.find((f) => f.key === params.get('cat'))?.key ?? 'all';
  const urlQ = params.get('q') ?? '';
  // The search box keeps its own text so typing stays smooth; the address follows it.
  const [q, setQ] = useState(urlQ);
  useEffect(() => {
    // The address changed by itself (Back, or the Store link): show its search.
    setQ((cur) => (cur.trim() === urlQ.trim() ? cur : urlQ));
  }, [urlQ]);
  const catalogRef = useRef<HTMLElement>(null);

  const products = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) ?? FILTERS[0];
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter(
      (p) =>
        p.categories.some(f.match) &&
        (!needle || p.name.toLowerCase().includes(needle) || p.tagline.toLowerCase().includes(needle) || p.tags.some((t) => t.includes(needle))),
    );
  }, [data, filter, q]);

  const pages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const page = Math.min(pages, Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1));
  const shown = products.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /** Changes some of the address's values (null removes one) and keeps the rest. */
  const setInAddress = (changes: Record<string, string | null>, replace: boolean) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace },
    );
  };
  const goToPage = (n: number) => {
    if (n !== page) setInAddress({ page: n > 1 ? String(n) : null }, false);
    catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // A new category or search starts again on page 1.
  const chooseFilter = (key: string) => setInAddress({ cat: key === 'all' ? null : key, page: null }, true);
  const search = (text: string) => {
    setQ(text);
    setInAddress({ q: text.trim() ? text : null, page: null }, true);
  };

  // Back from a product to a later page, a category or a search: show the list rather than
  // the top of the store. After the layout's own scroll to the top, hence the next frame.
  useEffect(() => {
    if (!params.has('page') && !params.has('cat') && !params.has('q')) return;
    const id = requestAnimationFrame(() => catalogRef.current?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(id);
    // Only when the store page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featured = data?.find((p) => p.featured) ?? data?.[0];
  const presentFilters = FILTERS.filter((f) => f.key === 'all' || (data ?? []).some((p) => p.categories.some(f.match)));

  return (
    <div className="container">
      <Hero product={featured} loading={isLoading} />

      <section ref={catalogRef} style={{ scrollMarginTop: 90 }}>
        <div className="toolbar">
          <div className="seg" role="tablist" aria-label="Categories">
            {presentFilters.map((f) => (
              <button key={f.key} role="tab" aria-selected={filter === f.key} className={filter === f.key ? 'on' : ''} onClick={() => chooseFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <label className="search">
            <IconSearch size={16} />
            <span className="sr-only">Search</span>
            <input className="input" placeholder="Search plugins" value={q} onChange={(e) => search(e.target.value)} />
          </label>
        </div>

        {error && <ErrorBox error={error} onRetry={() => refetch()} />}
        {isLoading && (
          <div className="grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 300 }} />
            ))}
          </div>
        )}
        {data && products.length === 0 && (
          <Empty title="Nothing matches">
            {q ? `No ${filter === 'all' ? 'products' : PRODUCT_CATEGORY_LABELS[filter as ProductCategory] ?? 'products'} match “${q}”.` : 'Check back soon.'}
          </Empty>
        )}
        <div className="grid">
          {shown.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
        <Pager page={page} pages={pages} onPage={goToPage} />
      </section>
    </div>
  );
}
